import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Receipt, Upload, Filter } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { InvoiceDetailPanel } from '@/features/finance/components/InvoiceDetailPanel';
import { InvoiceUploadDialog } from '@/features/finance/components/InvoiceUploadDialog';

interface Invoice {
  id: string;
  vendor: string;
  invoice_number: string;
  amount: number;
  currency: string;
  due_date: string;
  status: 'received' | 'under_review' | 'approved' | 'rejected' | 'paid';
  created_at: string;
}

const STATUS_CONFIG: Record<Invoice['status'], { label: string; variant: string }> = {
  received: { label: 'Received', variant: 'bg-gray-100 text-gray-700' },
  under_review: { label: 'Under Review', variant: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Approved', variant: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', variant: 'bg-red-100 text-red-700' },
  paid: { label: 'Paid', variant: 'bg-purple-100 text-purple-700' },
};

export function FinancePage() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const { data: invoices, isLoading, error, refetch } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('invoice')
        .select('*')
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      return (data ?? []).map((d) => ({
        id: d.id,
        vendor: d.vendor_name,
        invoice_number: d.invoice_number,
        amount: d.amount,
        currency: d.currency,
        due_date: d.due_date,
        status: d.status,
        created_at: d.created_at,
      })) as Invoice[];
    },
  });

  const filtered = useMemo(() => {
    if (!invoices) return [];
    return invoices.filter((inv) => {
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
      if (dateFrom && inv.due_date < dateFrom) return false;
      if (dateTo && inv.due_date > dateTo) return false;
      return true;
    });
  }, [invoices, statusFilter, dateFrom, dateTo]);

  const formatCurrency = (amount: number, currency: string) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  return (
    <div className="flex gap-6">
      <div className="flex-1 min-w-0">
        <PageHeader
          title="Finance"
          description="Manage invoices and financial documents"
          action={
            <Button onClick={() => setShowUpload(true)}>
              <Upload className="mr-2 h-4 w-4" /> Upload Invoice
            </Button>
          }
        />

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="flex flex-wrap items-end gap-4 pt-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters</span>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Due From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Due To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[160px]"
              />
            </div>
          </CardContent>
        </Card>

        {error && <ErrorAlert message={String(error)} onRetry={() => refetch()} />}

        {isLoading && (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <EmptyState
            icon={Receipt}
            title="No invoices found"
            description="Upload your first invoice to get started."
            actionLabel="Upload Invoice"
            onAction={() => setShowUpload(true)}
          />
        )}

        {filtered.length > 0 && (
          <div className="rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Vendor</th>
                  <th className="px-4 py-3 text-left font-medium">Invoice #</th>
                  <th className="px-4 py-3 text-right font-medium">Amount</th>
                  <th className="px-4 py-3 text-left font-medium">Due Date</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => {
                  const cfg = STATUS_CONFIG[inv.status];
                  return (
                    <tr
                      key={inv.id}
                      onClick={() => setSelectedInvoice(inv)}
                      className="cursor-pointer border-b transition-colors hover:bg-muted/30 last:border-b-0"
                    >
                      <td className="px-4 py-3 font-medium">{inv.vendor}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {inv.invoice_number}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatCurrency(inv.amount, inv.currency)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(inv.due_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={cfg.variant}>
                          {cfg.label}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <InvoiceUploadDialog
          open={showUpload}
          onOpenChange={setShowUpload}
          onUploaded={() => refetch()}
        />
      </div>

      {selectedInvoice && (
        <InvoiceDetailPanel
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onStatusChange={() => {
            setSelectedInvoice(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}
